// Libs
import React from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import { useHistory, useLocation } from 'react-router-dom';
// Utils
import { debounce } from 'utils/libs';
import CoreUtils from 'core/utils';
import {
	clearPricingSignalsInventoryValidationNavigationIntent,
	getPricingSignalsInventoryValidationNavigationIntent,
} from 'core/pricingSignalsInventoryValidationNavigation';
// Actions
import {
	getDatasourcePipelineErrorActions,
	mutate1Object as mutate1ObjectInCore,
	applyUserParams as createApplyUserParamsAction,
	mutateSubDataFilter,
	mutateUserParams,
	resetCustomFilterPropsInDataFilter,
	resetDataFilters,
	resetDatasource,
	resetUserParams as createResetUserParamsAction,
} from 'core/actions';
import { getMyDatasourceAccess } from 'components/Modules/actions';
// Selectors
import {
	selectDataFilters,
	selectDataFiltersIsFetching,
	selectDataFilterUserParams,
	selectSelectedDataFilterId,
	selectSelectedOrderTargetFilterId,
} from 'core/selectors';
// Components
import {
	Wrapper,
	LogoIcon,
	Tooltip,
	Caption,
	RadioV2,
	DatePicker,
	SectionTitle,
	Section,
	Span,
} from 'components';
// import { ActionBuilder } from 'core/ActionBuilder';

const styles = {
	container: {
		padding: '0',
		width: '100%',
		flexDirection: 'column',
		alignItems: 'stretch',
	},
	item: {
		padding: '0',
		margin: '0 0 5px 0',
	},
};

const getPickerName = picker => {
	if (picker === 'week') return 'Semana';
	if (picker === 'month') return 'Mes';
	if (picker === 'year') return 'Año';
	return 'Desde';
};

export const getDefaultDateUserParams = (params = {}, now = moment()) => {
	const defaultDateValue = params?.fromDateParam?.defaultDateValue;
	if (!defaultDateValue) return {};

	const defaultOffset = defaultDateValue.value ?? 1;
	const fromDateParam = now
		.clone()
		.subtract(defaultOffset, defaultDateValue.unit ?? 'day');
	const toDateParam = now.clone();
	const isTodayDateRange =
		Number(defaultOffset) === 0 &&
		params?.fromDateParam?.type === 'date' &&
		params?.toDateParam?.type === 'date';

	if (isTodayDateRange) {
		fromDateParam.startOf('day');
		toDateParam.endOf('day');
	}

	return { fromDateParam, toDateParam };
};

const DataFilterItem = React.memo(
	({
		style,
		filterItem,
		moduleItemId,
		selectedOrderTargetFilterId,
		selectedDataFilterId,
		onClick,
	}) => {
		const { id, name, description, actions } = filterItem;

		const isSelected = React.useMemo(
			() => id === selectedDataFilterId,
			[selectedDataFilterId],
		);

		return (
			<Wrapper style={style}>
				<Wrapper padding='0' width='100%'>
					<Tooltip title={description} placement='right'>
						<Wrapper
							padding='0'
							width='250px'
							height='20px'
							justifyContent='stretch'
							alignItems='center'
							overflow='hidden'
							onClick={onClick}
						>
							<RadioV2 isSelected={isSelected} />
							<Span width='100%' hoverable selected={isSelected}>
								{name}
							</Span>
						</Wrapper>
					</Tooltip>
					{/* {isSelected && moduleItemId && selectedOrderTargetFilterId && (
						<ActionBuilder
							moduleItemId={moduleItemId}
							orderTargetId={selectedOrderTargetFilterId}
						/>
					)} */}
				</Wrapper>
			</Wrapper>
		);
	},
);

export const _DatasourceAccessPicker = React.memo(
	({
		// Props
		moduleItemId,
		// State
		selectedDataFilterId,
		selectedOrderTargetFilterId,
		isFetching,
		dataFilters,
		dataFilterUserParams,
		// Actions
		getMyDatasourceAccess,
		mutate1ObjectInCore,
		applyUserParams,
		resetUserParams,
		resetCustomFilterPropsInDataFilter,
		resetDataFilters,
		resetDatasource,
	}) => {
		const { RangePicker } = DatePicker;
		const history = useHistory();
		const location = useLocation();

		const [seeMore, setSeeMore] = React.useState('hide'); // hide, show, selected, collapsed
		const [pendingRange, setPendingRange] = React.useState([]);
		const [rangeError, setRangeError] = React.useState();
		const navigationIntent = React.useMemo(
			() => getPricingSignalsInventoryValidationNavigationIntent(location),
			[location],
		);
		const pendingNavigationIntentRef = React.useRef(navigationIntent);
		const latestLocationRef = React.useRef(location);

		React.useEffect(() => {
			latestLocationRef.current = location;
		}, [location]);

		React.useEffect(() => {
			if (navigationIntent) {
				pendingNavigationIntentRef.current = navigationIntent;
			}
		}, [navigationIntent]);

		// Get available data filters
		const getDebouncedDataFilters = React.useCallback(
			debounce(callback => callback(), 200),
			[],
		);

		React.useEffect(() => {
			let isMounted = true;
			getDebouncedDataFilters(() => {
				if (!moduleItemId || !selectedOrderTargetFilterId) return;
				getMyDatasourceAccess({
					moduleItemId,
					orderTargetId: selectedOrderTargetFilterId,
					onLoading: () =>
						isMounted ? [resetDataFilters({ isFetching: true })] : [],
					onSuccess: dataFilters => {
						if (!isMounted) return [];
						const pendingNavigationIntent = pendingNavigationIntentRef.current;
						const navigationFilter = pendingNavigationIntent
							? dataFilters.find(
									df =>
										df.queryKey === pendingNavigationIntent.datasourceQueryKey,
								)
							: null;
						const defaultSelectedDataFilterItem =
							navigationFilter ||
							dataFilters.find(df => !!df?.config?.default) ||
							dataFilters[0];
						const defaultUserParams = getDefaultDateUserParams(
							defaultSelectedDataFilterItem?.params,
						);

						if (navigationFilter) {
							pendingNavigationIntentRef.current = null;
							clearPricingSignalsInventoryValidationNavigationIntent(
								history,
								latestLocationRef.current,
							);
						}

						return [
							resetDatasource(),
							createResetUserParamsAction(
								Array.from(
									CoreUtils.getRequiredDatasourceParamKeys(
										defaultSelectedDataFilterItem?.params,
									),
								),
							),
							mutate1ObjectInCore('dataFilters', {
								data: dataFilters,
								isFetching: false,
								selectedDataFilterId: defaultSelectedDataFilterItem?.id,
								reloadSubDataFilters: !!dataFilters.length,
								config: defaultSelectedDataFilterItem?.config || {},
							}),
							defaultSelectedDataFilterItem
								? createApplyUserParamsAction({
										...defaultUserParams,
										orderIdsParam: navigationFilter
											? pendingNavigationIntent.orderIds
											: [0],
									})
								: mutateUserParams({ orderIdsParam: [0] }),
						];
					},
					onError: () =>
						isMounted ? getDatasourcePipelineErrorActions('dataFilters') : [],
				});
			});
			return () => {
				isMounted = false;
				getDebouncedDataFilters.cancel();
			};
		}, [
			getDebouncedDataFilters,
			getMyDatasourceAccess,
			history,
			moduleItemId,
			mutate1ObjectInCore,
			resetDataFilters,
			resetDatasource,
			selectedOrderTargetFilterId,
		]);

		// See more
		React.useEffect(() => {
			if (dataFilters.length <= 3) setSeeMore('hide');
			else if (dataFilters.length > 3 && seeMore === 'hide') {
				setSeeMore('show');
			}
		}, [seeMore, dataFilters]);

		// Filter items with seeMore
		const filteredDataFilters = React.useMemo(() => {
			if (dataFilters.length > 3) {
				if (seeMore === 'selected') return dataFilters;
				if (seeMore === 'collapsed') setSeeMore('show');
				return dataFilters.filter((f, idx) => idx <= 2);
			}
			return dataFilters;
		}, [seeMore, dataFilters]);

		const containerStyle = React.useMemo(() => styles.container, [isFetching]);

		const filterParams = React.useMemo(
			() => dataFilters.find(df => df.id === selectedDataFilterId)?.params,
			[selectedDataFilterId, dataFilters],
		);
		const rangeWithTime = React.useMemo(
			() =>
				filterParams?.fromDateParam?.type === 'datetime' ||
				filterParams?.toDateParam?.type === 'datetime',
			[filterParams],
		);
		const rangeRequired = React.useMemo(
			() =>
				!!filterParams?.fromDateParam?.required ||
				!!filterParams?.toDateParam?.required,
			[filterParams],
		);
		const rangeLimit = React.useMemo(
			() => CoreUtils.getDatasourceRangeLimit({ params: filterParams }),
			[filterParams],
		);

		React.useEffect(() => {
			setPendingRange([]);
			setRangeError(undefined);
		}, [selectedDataFilterId]);

		return (
			<Section>
				<SectionTitle title='Filtro' empty={filteredDataFilters.length === 0} />
				<Wrapper {...containerStyle}>
					{isFetching && <LogoIcon spin={true} />}
					{!isFetching &&
						filteredDataFilters.map(filterItem => (
							<DataFilterItem
								key={filterItem.id}
								style={styles.item}
								filterItem={filterItem}
								moduleItemId={moduleItemId}
								selectedOrderTargetFilterId={selectedOrderTargetFilterId}
								selectedDataFilterId={selectedDataFilterId}
								onClick={() => {
									if (filterItem.id === selectedDataFilterId) return;
									resetDatasource();
									resetUserParams(
										Array.from(
											CoreUtils.getRequiredDatasourceParamKeys(
												filterItem.params,
											),
										),
									);
									mutate1ObjectInCore('dataFilters', {
										lastRowId: 0,
										selectedDataFilterId: filterItem.id,
										reloadSubDataFilters: true,
										config: filterItem?.config || {},
									});
									resetCustomFilterPropsInDataFilter(filterItem.id);
									applyUserParams({
										...getDefaultDateUserParams(filterItem.params),
										orderIdsParam: [0],
									});
								}}
							/>
						))}

					{seeMore === 'show' && seeMore !== 'selected' && (
						<Caption
							emphasis
							hoverable
							margin='10px 0 0 0'
							onClick={() => setSeeMore('selected')}
						>
							Ver más
						</Caption>
					)}
					{seeMore === 'selected' && (
						<Caption
							emphasis
							hoverable
							margin='10px 0 0 0'
							onClick={() => setSeeMore('collapsed')}
						>
							Ver menos
						</Caption>
					)}
				</Wrapper>

				{/* RANGE PICKER */}
				{filterParams?.fromDateParam && filterParams?.toDateParam && (
					<>
						<RangePicker
							style={{ marginTop: '10px' }}
							placeholder={['Desde', 'Hasta']}
							allowClear={!rangeRequired}
							showTime={rangeWithTime ? { format: 'HH:mm:ss' } : undefined}
							format={rangeWithTime ? 'YYYY-MM-DD HH:mm:ss' : undefined}
							value={[
								dataFilterUserParams.fromDateParam,
								dataFilterUserParams.toDateParam,
							]}
							onCalendarChange={range => setPendingRange(range || [])}
							disabledDate={current => {
								const anchorDate = pendingRange.find(Boolean);
								if (!anchorDate || rangeLimit.maxRangeDays === undefined)
									return false;
								if (!rangeLimit.valid) return true;
								return CoreUtils.isDatasourceRangeLimitExceeded({
									params: filterParams,
									fromValue: anchorDate,
									toValue: current,
								});
							}}
							onChange={range => {
								const [fromDateParam, toDateParam] = range || [];
								const validation = CoreUtils.validateDatasourceParams({
									params: {
										fromDateParam: filterParams.fromDateParam,
										toDateParam: filterParams.toDateParam,
									},
									dataFilterUserParams: {
										fromDateParam,
										toDateParam,
									},
								});
								if (!validation.valid) {
									setRangeError(
										validation.reason === 'maxRangeDays'
											? `El rango no puede superar ${validation.maxRangeDays} días.`
											: 'Seleccione un rango de fecha válido.',
									);
									return;
								}

								setPendingRange([]);
								setRangeError(undefined);
								applyUserParams({ fromDateParam, toDateParam });
							}}
						/>
						{rangeError && <Caption>{rangeError}</Caption>}
					</>
				)}

				{/* FROM DATE PICKER */}
				{filterParams?.fromDateParam && !filterParams?.toDateParam && (
					<DatePicker
						picker={filterParams?.fromDateParam.picker}
						style={{ marginTop: '10px' }}
						placeholder={getPickerName(filterParams?.fromDateParam.picker)}
						value={dataFilterUserParams.fromDateParam}
						onChange={fromDateParam => applyUserParams({ fromDateParam })}
					/>
				)}
				{/* TO DATE PICKER */}
				{filterParams?.toDateParam && !filterParams?.fromDateParam && (
					<DatePicker
						style={{ marginTop: '10px' }}
						placeholder='Hasta'
						value={dataFilterUserParams.toDateParam}
						onChange={toDateParam => applyUserParams({ toDateParam })}
					/>
				)}
			</Section>
		);
	},
);

const mapStateToProps = state => ({
	selectedOrderTargetFilterId: selectSelectedOrderTargetFilterId(state),
	selectedDataFilterId: selectSelectedDataFilterId(state),
	isFetching: selectDataFiltersIsFetching(state),
	dataFilters: selectDataFilters(state),
	dataFilterUserParams: selectDataFilterUserParams(state),
});
const actions = {
	getMyDatasourceAccess,
	mutate1ObjectInCore,
	applyUserParams: createApplyUserParamsAction,
	mutateSubDataFilter,
	resetUserParams: createResetUserParamsAction,
	resetCustomFilterPropsInDataFilter,
	resetDataFilters,
	resetDatasource,
};

export default connect(mapStateToProps, actions)(_DatasourceAccessPicker);
